import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixOnlineProgramsParticipantsV3JoinApplication from './online-programs-participants-v3-join-application-join-applications.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

/**
 * A JoinApplication tracks a member's request or invitation to join an online program, from application through approval, payment, and enrollment.
 *
 * Site owners can invite members, review applications, and manage payment-related activity. Members can apply to join a program, accept invitations, and complete payment when required.
 */
export interface JoinApplication {
  /**
   * JoinApplication ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.
   *
   * This API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the join application was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the join application was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Member ID of the site member applying or invited to participate in the program.
   *
   * Required when creating applications as a site owner with `manage_applications` permission.
   * Automatically set to the caller's member ID when members create their own applications.
   * @format GUID
   * @readonly
   */
  memberId?: string | null;
  /**
   * Program ID which this join application is related to.
   * @format GUID
   * @immutable
   */
  programId?: string;
  /**
   * Current status of the join application.
   *
   * Read-only for member-initiated applications.
   * Site owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Payment status of join application.
   * @readonly
   */
  paymentStatus?: PaymentStatusWithLiterals;
  /**
   * Payment info - represents associated Wix Payments or Pricing Plans data.
   * @readonly
   */
  paymentInfo?: PaymentInfo;
  /**
   * Read-only snapshot of member information from the Members API for `member_id`.
   * This is not the authoritative member record.
   * @readonly
   */
  member?: Member;
  /**
   * Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).
   * @format GUID
   * @readonly
   */
  participantId?: string | null;
  /**
   * Custom field data for the JoinApplication.
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)
   * must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Tags for categorizing and organizing join applications. */
  tags?: Tags;
}

export enum Status {
  /** Member has been invited to join the program and has not yet accepted. */
  INVITED = 'INVITED',
  /** Member's application is awaiting the site owner's decision. */
  APPROVAL_PENDING = 'APPROVAL_PENDING',
  /** Application is approved. Payment may still be required before the member is enrolled. */
  APPROVED = 'APPROVED',
  /** Member canceled the application. The service removes the join application asynchronously. */
  CANCELLED = 'CANCELLED',
  /** Site owner declined the application. The service removes the join application asynchronously. */
  DECLINED = 'DECLINED',
  /** Connected participant is suspended after the related Pricing Plans subscription expires. A successful new payment resumes the participation flow. */
  SUSPENDED = 'SUSPENDED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'INVITED'
  | 'APPROVAL_PENDING'
  | 'APPROVED'
  | 'CANCELLED'
  | 'DECLINED'
  | 'SUSPENDED';

export enum PaymentStatus {
  /** Payment isn't required because the program is free or the member has an eligible Pricing Plans entitlement. */
  NO_PAYMENT_REQUIRED = 'NO_PAYMENT_REQUIRED',
  /** Payment is required before the member can be enrolled. */
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  /** Payment checkout or a Pricing Plans purchase is in progress. */
  PAYMENT_IN_PROGRESS = 'PAYMENT_IN_PROGRESS',
  /** Member submitted an offline payment that awaits the site owner's decision. */
  PENDING_OWNER_APPROVAL = 'PENDING_OWNER_APPROVAL',
  /** Payment completed successfully. */
  PAYMENT_SUCCESSFUL = 'PAYMENT_SUCCESSFUL',
  /** Payment was declined by the payment system or, for an offline payment, by the site owner. */
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  /** Member canceled payment. */
  PAYMENT_CANCELED = 'PAYMENT_CANCELED',
}

/** @enumType */
export type PaymentStatusWithLiterals =
  | PaymentStatus
  | 'NO_PAYMENT_REQUIRED'
  | 'PAYMENT_PENDING'
  | 'PAYMENT_IN_PROGRESS'
  | 'PENDING_OWNER_APPROVAL'
  | 'PAYMENT_SUCCESSFUL'
  | 'PAYMENT_FAILED'
  | 'PAYMENT_CANCELED';

export interface PaymentInfo {
  /**
   * One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.
   * Wix Payments order id, populated when UoU selects One-time payment at the checkout
   * @format GUID
   * @readonly
   */
  paymentOrderId?: string | null;
  /**
   * Offline transaction id, populated when UoU selects manual payment type at the checkout
   * @format GUID
   * @readonly
   */
  offlineTransactionId?: string | null;
  /**
   * Pricing Plans IDs connected to the program, populated when UoU selects Pricing Plans at the checkout
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  paidPlanIds?: string[];
  /**
   * Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon
   * @format GUID
   * @readonly
   */
  couponId?: string | null;
}

export interface Member {
  /**
   * Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.
   * Member contact's first name
   * @readonly
   * @maxLength 1000
   */
  firstName?: string | null;
  /**
   * Member contact's last name
   * @readonly
   * @maxLength 1000
   */
  lastName?: string | null;
  /**
   * Member login email
   * @readonly
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Member contact id
   * @format GUID
   * @readonly
   */
  contactId?: string | null;
  /**
   * Member nickname
   * @readonly
   * @maxLength 1000
   */
  nickname?: string | null;
  /**
   * Member profile image url
   * @readonly
   * @format WEB_URL
   */
  profileImageUrl?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, typically restricted from site members and visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone with access to the entity, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

/**
 * Message for reindexing search data to a given search schema. Support both upsert and delete flows as well as
 * performs context manipulation with adding tenant, provided in message to callscope.
 */
export interface ReindexMessage extends ReindexMessageActionOneOf {
  upsert?: Upsert;
  delete?: Delete;
  entityFqdn?: string;
  tenantId?: string;
  eventTime?: Date | null;
  entityEventSequence?: string | null;
  schema?: Schema;
}

/** @oneof */
export interface ReindexMessageActionOneOf {
  upsert?: Upsert;
  delete?: Delete;
}

export interface Upsert {
  entityId?: string;
  entityAsJson?: string;
}

export interface Delete {
  entityId?: string;
}

export interface Schema {
  label?: string;
  clusterName?: string;
}

/** Domain event emitted when tags are modified */
export interface JoinApplicationTagsModified {
  /** The JoinApplication with updated tags */
  joinApplication?: JoinApplication;
  /** Tags that were assigned */
  assignedTags?: Tags;
  /** Tags that were unassigned */
  unassignedTags?: Tags;
}

/** Domain event emitted when status is modified */
export interface StatusChanged {
  /** The JoinApplication with updated status */
  joinApplication?: JoinApplication;
  /** Previous status */
  previousStatus?: StatusWithLiterals;
}

/** Domain event emitted when payment status is modified */
export interface PaymentStatusChanged {
  /** The JoinApplication with updated payment status */
  joinApplication?: JoinApplication;
  /** Previous payment status */
  previousPaymentStatus?: PaymentStatusWithLiterals;
}

export interface CreateJoinApplicationRequest {
  /** JoinApplication to be created. */
  joinApplication: JoinApplication;
}

export interface CreateJoinApplicationResponse {
  /** The created JoinApplication. */
  joinApplication?: JoinApplication;
}

export interface GetJoinApplicationRequest {
  /**
   * ID of the JoinApplication to retrieve.
   * @format GUID
   */
  joinApplicationId: string;
}

export interface GetJoinApplicationResponse {
  /** The requested JoinApplication. */
  joinApplication?: JoinApplication;
}

export interface DeleteJoinApplicationRequest {
  /**
   * Id of the JoinApplication to delete.
   * @format GUID
   */
  joinApplicationId: string;
}

export interface DeleteJoinApplicationResponse {}

export interface QueryJoinApplicationsRequest {
  /** WQL expression. */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 100
   */
  sort?: Sorting[];
  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 1000
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 1000
   */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryJoinApplicationsResponse {
  /** List of JoinApplications. */
  joinApplications?: JoinApplication[];
  /** Paging metadata */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchJoinApplicationsRequest {
  /** WQL expression. */
  search?: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Aggregations are a way to explore large amounts of data by displaying summaries about various partitions of the data and later allowing to narrow the navigation to a specific partition.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * UTC offset or IANA time zone. Valid values are
   * ISO 8601 UTC offsets, such as +02:00 or -06:00,
   * and IANA time zone IDs, such as Europe/Rome.
   *
   * Affects all filters and aggregations returned values.
   * You may override this behavior in a specific filter by providing
   * timestamps including time zone. For example, `"2023-12-20T10:52:34.795Z"`.
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation. */
  nested?: NestedAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not provided. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not provided. */
  to?: number | null;
}

export enum SortType {
  /** Sort by number of matches. */
  COUNT = 'COUNT',
  /** Sort by value of the field alphabetically. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Sort in descending order. */
  DESC = 'DESC',
  /** Sort in ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Exclude missing values from the aggregation results. */
  EXCLUDE = 'EXCLUDE',
  /** Include missing values in the aggregation results. */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Count of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
  /**
   * Sum of values.
   * @internal
   */
  SUM = 'SUM',
  /**
   * Average of values.
   * @internal
   */
  AVG = 'AVG',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values. */
  includeOptions?: IncludeMissingValuesOptions;
  /** Whether to sort by number of matches or value of the field. */
  sortType?: SortTypeWithLiterals;
  /** Whether to sort in ascending or descending order. */
  sortDirection?: SortDirectionWithLiterals;
  /** How many aggregations to return. Can be between 1 and 250. 10 is the default. */
  limit?: number | null;
  /** Whether to include or exclude missing values from the aggregation results. Default: `EXCLUDE`. */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values. */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket. */
  RANGE = 'RANGE',
  /** A single-value metric aggregation. For example, min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets, where during aggregation each entity will be placed in the first bucket its value falls into, based on the provided range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Define the operator for the scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation. */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Yearly interval */
  YEAR = 'YEAR',
  /** Monthly interval */
  MONTH = 'MONTH',
  /** Weekly interval */
  WEEK = 'WEEK',
  /** Daily interval */
  DAY = 'DAY',
  /** Hourly interval */
  HOUR = 'HOUR',
  /** Minute interval */
  MINUTE = 'MINUTE',
  /** Second interval */
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket. */
  RANGE = 'RANGE',
  /** A single-value metric aggregation. For example, min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.) */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Multi-level aggregation, where each next aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation expressed through a list of aggregation where each next aggregation is nested within previous one. */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each next aggregation is nested within previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface SearchDetails {
  /** Defines how separate search terms in `expression` are combined. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If empty - will search in all searchable fields. Use dot notation to specify json path.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm). */
  fuzzy?: boolean;
}

export enum Mode {
  /** Any of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchJoinApplicationsResponse {
  /** List of JoinApplications. */
  joinApplications?: JoinApplication[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
  /** Aggregation data */
  aggregationData?: AggregationData;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface AggregationData {
  /**
   * key = aggregation name (as derived from search request).
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * User-defined name of aggregation, matches the one provided in request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that matches result. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, matches the one provided in request.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of value aggregations.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in ISO 8601 format.
   * @maxLength 100
   */
  value?: string;
  /** Count of documents in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form.
 * Aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregations.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * User-defined name of aggregation as derived from search request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that must match provided kind as derived from search request. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by as derived from search request.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface BulkCreateJoinApplicationsRequest {
  /**
   * List of JoinApplications to be created
   * @minSize 1
   * @maxSize 100
   */
  joinApplications: JoinApplication[];
  /** set to `true` if you wish to receive back the created JoinApplications in the response */
  returnEntity?: boolean;
}

export interface BulkCreateJoinApplicationsResponse {
  /**
   * List of the bulk create operation results including the JoinApplications and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkJoinApplicationResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkJoinApplicationResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: JoinApplication;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpsertJoinApplicationMigrationRequest {
  /**
   * List of join applications to create or update.
   * @minSize 1
   * @maxSize 100
   */
  joinApplications?: JoinApplicationMigration[];
}

export interface JoinApplicationMigration {
  /** JoinApplication to create or update. */
  joinApplication?: JoinApplication;
}

export interface BulkUpsertJoinApplicationMigrationResponse {
  /**
   * Results of the bulk migration operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkJoinApplicationMigrationResult[];
  /** Metadata about the bulk action. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkJoinApplicationMigrationResult {
  /** Metadata regarding the specific single upsert operation. */
  itemMetadata?: ItemMetadata;
  /** Action type of bulk action (INSERT or UPDATE) for that endpoint. */
  action?: BulkActionTypeWithLiterals;
}

export enum BulkActionType {
  INSERT = 'INSERT',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

/** @enumType */
export type BulkActionTypeWithLiterals =
  | BulkActionType
  | 'INSERT'
  | 'UPDATE'
  | 'DELETE';

export interface BulkDeleteJoinApplicationsRequest {
  /**
   * JoinApplication ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  joinApplicationIds: string[];
}

export interface BulkDeleteJoinApplicationsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteJoinApplicationsResponseBulkJoinApplicationResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteJoinApplicationsResponseBulkJoinApplicationResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface GetCurrentJoinApplicationRequest {
  /**
   * Program ID to look up the caller's join application for
   * @format GUID
   */
  programId: string;
}

export interface GetCurrentJoinApplicationResponse {
  /** The found JoinApplication (unique per member+program). */
  joinApplication?: JoinApplication;
}

export interface MyJoinApplicationRequest {
  /**
   * Program ID to look up the caller's join application for
   * @format GUID
   */
  programId: string;
}

export interface MyJoinApplicationResponse {
  /** The found JoinApplication (unique per member+program). */
  joinApplication?: JoinApplication;
}

export interface ApproveJoinApplicationRequest {
  /**
   * ID of the JoinApplication to approve
   * @format GUID
   */
  joinApplicationId: string;
}

export interface ApproveJoinApplicationResponse {
  /** The approved JoinApplication with updated status */
  joinApplication?: JoinApplication;
}

export interface DeclineJoinApplicationRequest {
  /**
   * ID of the JoinApplication to decline
   * @format GUID
   */
  joinApplicationId: string;
}

export interface DeclineJoinApplicationResponse {
  /** The declined JoinApplication with updated status */
  joinApplication?: JoinApplication;
}

export interface CancelJoinApplicationRequest {
  /**
   * ID of the JoinApplication to cancel
   * @format GUID
   */
  joinApplicationId: string;
}

export interface CancelJoinApplicationResponse {
  /** The cancelled JoinApplication with updated status */
  joinApplication?: JoinApplication;
}

export interface AcceptInviteRequest {
  /**
   * ID of the JoinApplication to accept
   * @format GUID
   */
  joinApplicationId: string;
}

export interface AcceptInviteResponse {
  /** The JoinApplication with status updated from INVITED to APPROVED */
  joinApplication?: JoinApplication;
}

export interface BulkUpdateJoinApplicationTagsRequest {
  /**
   * JoinApplication IDs to update tags for (1-100 items)
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  joinApplicationIds: string[];
  /** Tags to assign to the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  assignTags?: Tags;
  /** Tags to unassign from the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  unassignTags?: Tags;
}

export interface BulkUpdateJoinApplicationTagsResponse {
  /**
   * Results for each JoinApplication
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateJoinApplicationTagsResult[];
  /** Metadata about the bulk operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateJoinApplicationTagsResult {
  /** Metadata for this specific item */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateJoinApplicationTagsByFilterRequest {
  /** WQL filter to select JoinApplications */
  filter: Record<string, any> | null;
  /** Tags to assign to matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  assignTags?: Tags;
  /** Tags to unassign from matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  unassignTags?: Tags;
}

export interface BulkUpdateJoinApplicationTagsByFilterResponse {
  /**
   * Job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
   * @format GUID
   */
  jobId?: string;
}

export interface PreparePaymentRequest {
  /**
   * ID of the JoinApplication to prepare payment for
   * @format GUID
   */
  joinApplicationId: string;
  /** Type of payment to prepare */
  paymentType: PaymentTypeWithLiterals;
}

export enum PaymentType {
  /** One-time payment through Wix Payments. */
  SINGLE_PAYMENT = 'SINGLE_PAYMENT',
  /** Payment through Wix Pricing Plans. */
  PAID_PLANS = 'PAID_PLANS',
}

/** @enumType */
export type PaymentTypeWithLiterals =
  | PaymentType
  | 'SINGLE_PAYMENT'
  | 'PAID_PLANS';

export interface PreparePaymentResponse {
  /** JoinApplication with payment info refreshed and payment status set to PAYMENT_IN_PROGRESS. */
  joinApplication?: JoinApplication;
}

export interface ApplyCouponRequest {
  /**
   * ID of the JoinApplication to apply the coupon to
   * @format GUID
   */
  joinApplicationId: string;
  /**
   * Coupon code to apply
   * @maxLength 100
   */
  couponCode: string;
}

export interface ApplyCouponResponse {
  /**
   * Coupon ID that was applied
   * @format GUID
   */
  couponId?: string;
  /**
   * Original order subtotal before discount, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  subTotal?: string;
  /**
   * Discount amount applied, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  discount?: string;
  /**
   * Final total after discount, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  total?: string;
}

export interface RemoveCouponRequest {
  /**
   * ID of the JoinApplication to remove the coupon from
   * @format GUID
   */
  joinApplicationId: string;
  /**
   * ID of the coupon to remove (must match the coupon applied to the order)
   * @format GUID
   */
  couponId: string;
}

export interface RemoveCouponResponse {
  /**
   * Final total after coupon removal, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  total?: string;
}

export interface CompleteFreeCouponPaymentRequest {
  /**
   * ID of the JoinApplication to complete free coupon payment for
   * @format GUID
   */
  joinApplicationId: string;
}

export interface CompleteFreeCouponPaymentResponse {
  /** The JoinApplication with payment status updated to PAYMENT_SUCCESSFUL */
  joinApplication?: JoinApplication;
}

export interface BulkInviteMembersToProgramByFilterRequest {
  /**
   * Program to which bulk invite members.
   * @format GUID
   */
  programId: string;
  /**
   * Exclude site members.
   * @format GUID
   * @maxSize 100
   */
  excludeMemberIds?: string[];
}

export interface BulkInviteMembersToProgramByFilterResponse {
  /**
   * Job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
   * @format GUID
   */
  jobId?: string;
}

export interface DeleteAllJoinApplicationsMigrationRequest {}

export interface DeleteAllJoinApplicationsMigrationResponse {}

export interface ReindexJoinApplicationsSearchRequest {
  /**
   * @minSize 1
   * @maxSize 1000
   * @format GUID
   */
  joinApplicationIds?: string[];
}

export interface ReindexJoinApplicationsSearchResponse {}

export interface SyncPaymentExpirationTasksMigrationRequest {}

export interface SyncPaymentExpirationTasksMigrationResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

/** This webhook is triggered whenever a payment transaction is updated (including refunds) via specific [Wix Apps](https://dev.wix.com/api/rest/wix-payments/about-wix-payments). */
export interface PaymentEvent extends PaymentEventEventOneOf {
  /** `TRANSACTION_STATUS_CHANGED` event details. */
  transactionStatusChangedEvent?: TransactionStatusChangedEvent;
  /** `REFUND_STATUS_CHANGED` event details. */
  refundStatusChangedEvent?: RefundStatusChangedEvent;
  /**
   * `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @deprecated `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @targetRemovalDate 2026-06-30
   */
  recurringPaymentStatusChangedEvent?: RecurringPaymentStatusChangedEvent;
  /** `TRANSACTION_UPDATED` event details. */
  transactionUpdatedEvent?: TransactionUpdatedEvent;
  /** `TRANSACTION_CREATED` event details. */
  transactionCreatedEvent?: TransactionCreatedEvent;
  /**
   * Unique event ID.
   * @format GUID
   */
  _id?: string;
  /** Event timestamp */
  eventTime?: Date | null;
  /**
   * ID of the [Wix app](https://dev.wix.com/docs/rest/articles/getting-started/wix-business-solutions) that triggered this event.
   * @maxLength 100
   */
  wixAppId?: string;
  /**
   * Unique ID assigned to each Wix app in each site.
   * @format GUID
   */
  wixAppInstanceId?: string;
  /** Event type. */
  eventType?: EventTypeWithLiterals;
  /**
   * ID of Wix app that mediated between Wix Payments and the Wix app that triggered the event (if relevant).
   * @maxLength 100
   */
  managingWixAppId?: string | null;
}

/** @oneof */
export interface PaymentEventEventOneOf {
  /** `TRANSACTION_STATUS_CHANGED` event details. */
  transactionStatusChangedEvent?: TransactionStatusChangedEvent;
  /** `REFUND_STATUS_CHANGED` event details. */
  refundStatusChangedEvent?: RefundStatusChangedEvent;
  /**
   * `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @deprecated `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @targetRemovalDate 2026-06-30
   */
  recurringPaymentStatusChangedEvent?: RecurringPaymentStatusChangedEvent;
  /** `TRANSACTION_UPDATED` event details. */
  transactionUpdatedEvent?: TransactionUpdatedEvent;
  /** `TRANSACTION_CREATED` event details. */
  transactionCreatedEvent?: TransactionCreatedEvent;
}

export enum EventType {
  /** Fired when a transaction's status changes. */
  TRANSACTION_STATUS_CHANGED = 'TRANSACTION_STATUS_CHANGED',
  /** Fired when a refund's status changes. */
  REFUND_STATUS_CHANGED = 'REFUND_STATUS_CHANGED',
  /** Fired when a legacy subscription's status changes. */
  RECURRING_PAYMENT_STATUS_CHANGED = 'RECURRING_PAYMENT_STATUS_CHANGED',
  /** Fired when a transaction is updated. */
  TRANSACTION_UPDATED = 'TRANSACTION_UPDATED',
  /** Fired when a transaction is created. */
  TRANSACTION_CREATED = 'TRANSACTION_CREATED',
}

/** @enumType */
export type EventTypeWithLiterals =
  | EventType
  | 'TRANSACTION_STATUS_CHANGED'
  | 'REFUND_STATUS_CHANGED'
  | 'RECURRING_PAYMENT_STATUS_CHANGED'
  | 'TRANSACTION_UPDATED'
  | 'TRANSACTION_CREATED';

export interface TransactionStatusChangedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** New transaction details. */
  transaction?: Transaction;
  /** Previous transaction status. */
  previousStatus?: TransactionStatusWithLiterals;
}

export interface Order {
  _id?: string;
  /** Wix app order ID */
  wixAppOrderId?: string;
  /** Wix app buyer ID */
  wixAppBuyerId?: string | null;
  /** Items included in the order */
  items?: OrderItem[];
  /**
   * total number of items in order
   * items_total_count > `items`.size if `items` doesn't contain all items
   */
  itemsTotalCount?: number;
  /** Additional charges to the order - e.g., tax, shipping or discount (optional) */
  additionalCharges?: OrderAdditionalCharges;
  /** Customer's shipping address (optional) */
  shippingAddress?: Address;
  /** Customer's shipping address details (optional) */
  shippingAddressContacts?: FullAddressContactDetails;
  /** Order creation date */
  createdAt?: Date | null;
  /** External data which is stored with the order */
  externalData?: Record<string, string>;
  /**
   * Vertical invoice ID
   * @maxLength 200
   */
  verticalInvoiceId?: string | null;
  /** the part of total amount taken by platform from the amount sent to merchant */
  platformFee?: number | null;
  /**
   * ID of the buyer's contact record in the contacts system.
   * @format GUID
   */
  contactId?: string | null;
  /** Indicates that this order is a test order. Test orders are simulated and do not move real money. */
  test?: boolean | null;
}

/** Item included in order */
export interface OrderItem {
  /** Order item ID (required) */
  _id?: string;
  /** Order item name (required) */
  name?: string;
  /** Quantity (required) */
  quantity?: number;
  /** Total price charged for this item in the order - quantity * price (required) */
  price?: number;
  /** Order item description */
  description?: string;
  /** Weight of one item */
  weightInKg?: number;
  /** Whether the item is a physical a digital product */
  category?: OrderItemCategoryWithLiterals;
  /**
   * UN/CEFACT unit of measure code for the order item, following the UNECE Recommendation 20 standard (e.g., "EA", "KG")
   * @minLength 2
   * @maxLength 3
   */
  unitOfMeasureCode?: string | null;
  /**
   * UNSPSC commodity code classifying the type of product or service
   * @minLength 8
   * @maxLength 8
   */
  commodityCode?: string | null;
  /**
   * Merchant-defined product code or SKU identifier for the order item
   * @minLength 1
   * @maxLength 127
   */
  productCode?: string | null;
  /** Per-item tax amount */
  tax?: number | null;
  /** Per-item discount amount */
  discount?: number | null;
  /** Whether the item is a donation. When true, PayPal's category will be emitted as "DONATION". */
  donation?: boolean;
}

/** Order item type */
export enum OrderItemCategory {
  UNDEFINED = 'UNDEFINED',
  /** Physical product */
  PHYSICAL = 'PHYSICAL',
  /** Digital product */
  DIGITAL = 'DIGITAL',
}

/** @enumType */
export type OrderItemCategoryWithLiterals =
  | OrderItemCategory
  | 'UNDEFINED'
  | 'PHYSICAL'
  | 'DIGITAL';

/** Describing any additional charges in the order. They are not required, but can't be added later. */
export interface OrderAdditionalCharges {
  /** Absolute taxes charged to the order */
  tax?: number;
  /** Shipping cost charged to the order */
  shipping?: number;
  /** Discount amount applied to the order */
  discount?: number;
  /**
   * Application fee
   * @deprecated Application fee
   * @replacedBy order.platform_fee
   * @targetRemovalDate 2025-12-01
   */
  applicationFee?: number;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /**
   * A string containing the full address of this location.
   * @internal
   */
  formatted?: string | null;
  /**
   * Free text to help find the address.
   * @internal
   */
  hint?: string | null;
  /**
   * Coordinates of the physical address.
   * @internal
   */
  location?: AddressLocation;
  /**
   * Country full name.
   * @internal
   */
  countryFullname?: string | null;
  /**
   * Subdivision full name.
   * @internal
   */
  subdivisionFullname?: string | null;
  /**
   * Multi-level subdivisions from top to bottom.
   * @internal
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
  /**
   * Optional address line 1
   * @internal
   */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
  /**
   * Subdivision level
   * @internal
   */
  type?: SubdivisionTypeWithLiterals;
  /**
   * Free text description of subdivision type.
   * @internal
   */
  typeInfo?: string | null;
  /**
   * Standard organizations details (e.g. ISO).
   * @internal
   */
  standardDetails?: StandardDetails;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

/** Full contact details for an address */
export interface FullAddressContactDetails {
  /** Contact's first name. */
  firstName?: string | null;
  /** Contact's last name. */
  lastName?: string | null;
  /**
   * Contact's full name.
   * @internal
   */
  fullName?: string | null;
  /**
   * Contact's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /** Contact's company name. */
  company?: string | null;
  /**
   * Email associated with the address.
   * @format EMAIL
   */
  email?: string | null;
  /** Tax info. Currently usable only in Brazil. */
  vatId?: VatId;
}

export interface VatId {
  /** Customer's tax ID. */
  _id?: string;
  /**
   * Tax type.
   *
   * Supported values:
   * + `CPF`: for individual tax payers
   * + `CNPJ`: for corporations
   */
  type?: VatTypeWithLiterals;
}

/** tax info types */
export enum VatType {
  UNSPECIFIED = 'UNSPECIFIED',
  /** CPF - for individual tax payers. */
  CPF = 'CPF',
  /** CNPJ - for corporations */
  CNPJ = 'CNPJ',
}

/** @enumType */
export type VatTypeWithLiterals = VatType | 'UNSPECIFIED' | 'CPF' | 'CNPJ';

export interface Transaction extends TransactionPaymentMethodDataOneOf {
  creditCardPaymentMethodData?: CreditCardPaymentMethodData;
  /** Transaction ID */
  _id?: string;
  /** Provider transaction ID */
  providerTransactionId?: string | null;
  /** Amount and currency of transaction */
  amount?: CurrencyAmount;
  /** Refunded amount and currency of transaction */
  refundedAmount?: CurrencyAmount;
  /** Updated transaction status */
  status?: TransactionStatusWithLiterals;
  /** Reason provided for status change */
  reasonCode?: string | null;
  /** Payment method */
  paymentMethod?: string;
  /** Payment service provider */
  paymentProvider?: string;
  /** Customer's billing address (optional) */
  billingAddress?: Address;
  /** Customer's billing address - contact details (optional) */
  billingAddressContacts?: FullAddressContactDetails;
  /** Details of subscription, which (if) the transaction is a part of */
  recurringPaymentDetails?: RecurringPaymentDetails;
  /** The details of a payment method account. */
  paymentMethodAccountInfo?: V3AccountInfo;
  /** Transaction creation date */
  createdAt?: Date | null;
  /** Total number of installments */
  installments?: number;
  /** Scheduled action for this transaction */
  scheduledAction?: ScheduledAction;
  /**
   * order id associated with the transaction on the vertical side
   * @minLength 1
   * @maxLength 100
   */
  verticalOrderId?: string | null;
  appliedPlatformFee?: number | null;
  /**
   * Whether the transaction is from a sandbox (test) environment or production (live) environment.
   * @readonly
   */
  sandbox?: boolean | null;
  /**
   * ID correlating all attempts of one logical charge within a failover chain. Internal use only.
   * @internal
   * @format GUID
   * @readonly
   */
  failoverChainId?: string | null;
  /**
   * Number of failovers that preceded this attempt within its failover chain. 0 = the original attempt (no failover occurred); N = this transaction is the result of the Nth failover. Internal use only.
   * @internal
   * @max 2
   * @readonly
   */
  failoverCount?: number | null;
}

/** @oneof */
export interface TransactionPaymentMethodDataOneOf {
  creditCardPaymentMethodData?: CreditCardPaymentMethodData;
}

export interface CurrencyAmount {
  /** Amount */
  amount?: number;
  currency?: string;
}

export enum TransactionStatus {
  UNDEFINED = 'UNDEFINED',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  PENDING_MERCHANT = 'PENDING_MERCHANT',
  CANCELED = 'CANCELED',
  DECLINED = 'DECLINED',
  REFUNDED = 'REFUNDED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  AUTHORIZED = 'AUTHORIZED',
  VOIDED = 'VOIDED',
}

/** @enumType */
export type TransactionStatusWithLiterals =
  | TransactionStatus
  | 'UNDEFINED'
  | 'APPROVED'
  | 'PENDING'
  | 'PENDING_MERCHANT'
  | 'CANCELED'
  | 'DECLINED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED'
  | 'AUTHORIZED'
  | 'VOIDED';

export interface RecurringPaymentDetails {
  /** Current cycle number (Recurring payments only) */
  cycleNumber?: number;
}

export interface CreditCardPaymentMethodData {
  /** Card issuing network */
  network?: string;
  /** Card number with only bin and last4 positions showed (non-PCI data) */
  maskedCreditCard?: string;
  /** Card holder's full name specified on the card. */
  holderName?: string;
  /** Card expiration month. */
  expiryMonth?: string;
  /**
   * Card expiration year.
   * @minLength 4
   * @maxLength 4
   */
  expiryYear?: string | null;
  /**
   * Card last 4 digits.
   * @minLength 4
   * @maxLength 4
   */
  lastFourDigits?: string | null;
  /**
   * Card BIN (Bank Identification Number). It's the first 4-8 digits of a card number.
   * @minLength 4
   * @maxLength 8
   */
  bin?: string | null;
}

export interface V3AccountInfo {
  /**
   * The email of an Account used by Payment Method
   * @format EMAIL
   */
  email?: string | null;
}

export interface ScheduledAction {
  /** type of the action */
  type?: ScheduledActionTypeWithLiterals;
  /** the date and time of the action */
  executionDate?: Date | null;
}

export enum ScheduledActionType {
  VOID = 'VOID',
  CAPTURE = 'CAPTURE',
}

/** @enumType */
export type ScheduledActionTypeWithLiterals =
  | ScheduledActionType
  | 'VOID'
  | 'CAPTURE';

export interface RefundStatusChangedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** Updated transaction details. */
  transaction?: Transaction;
  /** Updated refund details. */
  refund?: Refund;
  /** Previous refund status. */
  previousStatus?: RefundStatusWithLiterals;
}

export interface Refund {
  _id?: string;
  /** Amount and currency of refund */
  amount?: CurrencyAmount;
  /** Updated refund status */
  status?: RefundStatusWithLiterals;
  /** Reason provided for status change */
  reasonCode?: string | null;
  /** Refund creation date */
  createdAt?: Date | null;
}

export enum RefundStatus {
  UNDEFINED = 'UNDEFINED',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
}

/** @enumType */
export type RefundStatusWithLiterals =
  | RefundStatus
  | 'UNDEFINED'
  | 'APPROVED'
  | 'PENDING'
  | 'DECLINED';

export interface RecurringPaymentStatusChangedEvent {
  /** Order details(limited to 30 items). */
  order?: Order;
  /** Recurring payment status. */
  status?: RecurringPaymentStatusStatusWithLiterals;
  /** Previous recurring payment status. */
  previousStatus?: RecurringPaymentStatusStatusWithLiterals;
  /** Details of RecurringPayment cancellation (empty if `status` is not CANCELLED). */
  cancellationDetails?: RecurringPaymentCancellationDetails;
}

export enum RecurringPaymentStatusStatus {
  UNDEFINED = 'UNDEFINED',
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  FINISHED = 'FINISHED',
  SUSPENDED = 'SUSPENDED',
}

/** @enumType */
export type RecurringPaymentStatusStatusWithLiterals =
  | RecurringPaymentStatusStatus
  | 'UNDEFINED'
  | 'PENDING'
  | 'ACTIVE'
  | 'CANCELLED'
  | 'FINISHED'
  | 'SUSPENDED';

export interface RecurringPaymentCancellationDetails {
  /** Initiator of cancellation */
  initiator?: InitiatorWithLiterals;
  /** Successfully paid recurring cycles count before the cancellation */
  paidRegularCycleCount?: number;
}

export enum Initiator {
  UNDEFINED = 'UNDEFINED',
  BUYER = 'BUYER',
  MERCHANT = 'MERCHANT',
  PAYMENT = 'PAYMENT',
  SETUP = 'SETUP',
}

/** @enumType */
export type InitiatorWithLiterals =
  | Initiator
  | 'UNDEFINED'
  | 'BUYER'
  | 'MERCHANT'
  | 'PAYMENT'
  | 'SETUP';

export interface TransactionUpdatedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** Updated transaction details. */
  transaction?: Transaction;
  /** Is event triggered by request to clean user data */
  triggeredByAnonymizeRequest?: boolean | null;
}

export interface TransactionCreatedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** Updated transaction details. */
  transaction?: Transaction;
}

export interface BenefitNotification {
  /**
   * Plan unique ID
   * @format GUID
   */
  planId?: string;
  /**
   * App def ID
   * @format GUID
   */
  appDefId?: string;
  /** Current benefit details */
  benefit?: Benefit;
  /** Previous benefit */
  prevBenefit?: Benefit;
  /** Notification event */
  event?: EventWithLiterals;
}

export interface Benefit {
  /**
   * Benefit unique ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Benefit Type */
  benefitType?: BenefitTypeWithLiterals;
  /**
   * Resource IDs that serves by this benefit
   * @format GUID
   */
  resourceIds?: string[];
  /** Amount of credits that provided by this benefit */
  creditAmount?: number | null;
  /**
   * additional details related to benefit; limited to 20 entries, 20 symbols for key and 20 symbols for value
   * @maxSize 20
   */
  customFields?: Record<string, string>;
  /** return value only in case it required in the ListRequest, true means that benefit's type could be updated */
  editable?: boolean | null;
  /** Benefit behavior */
  behavior?: Behavior;
  /**
   * Id of the app associated with this benefit
   * @format GUID
   * @readonly
   */
  appDefId?: string | null;
}

export interface EntryPass {}

export interface Discount extends DiscountDiscountOneOf {
  /**
   * Fixed-rate percent off discount
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentOffRate?: string;
  /**
   * Absolute amount discount
   * @decimalValue options { gt:0, maxScale:2 }
   */
  moneyOffAmount?: string;
}

/** @oneof */
export interface DiscountDiscountOneOf {
  /**
   * Fixed-rate percent off discount
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentOffRate?: string;
  /**
   * Absolute amount discount
   * @decimalValue options { gt:0, maxScale:2 }
   */
  moneyOffAmount?: string;
}

export enum BenefitType {
  /** Should never be used */
  UNDEFINED = 'UNDEFINED',
  /** Limited benefit type */
  LIMITED = 'LIMITED',
  /** Unlimited benefit type */
  UNLIMITED = 'UNLIMITED',
}

/** @enumType */
export type BenefitTypeWithLiterals =
  | BenefitType
  | 'UNDEFINED'
  | 'LIMITED'
  | 'UNLIMITED';

export interface Behavior extends BehaviorBehaviorOneOf {
  /** Entry pass for resources, e.g. a ticket for Bookings service or a ticket for Events. */
  defaultBehavior?: EntryPass;
  /** Discount applied to paid resources */
  discount?: Discount;
}

/** @oneof */
export interface BehaviorBehaviorOneOf {
  /** Entry pass for resources, e.g. a ticket for Bookings service or a ticket for Events. */
  defaultBehavior?: EntryPass;
  /** Discount applied to paid resources */
  discount?: Discount;
}

export enum Event {
  Updated = 'Updated',
  Deleted = 'Deleted',
  Created = 'Created',
}

/** @enumType */
export type EventWithLiterals = Event | 'Updated' | 'Deleted' | 'Created';

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateJoinApplicationApplicationErrors =
  | {
      code?: 'PROGRAM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PROGRAM_NOT_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MISSING_MEMBER_ID';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkCreateJoinApplicationsApplicationErrors =
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type GetCurrentJoinApplicationApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type MyJoinApplicationApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type ApproveJoinApplicationApplicationErrors =
  | {
      code?: 'INVALID_JOIN_APPLICATION_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type DeclineJoinApplicationApplicationErrors = {
  code?: 'INVALID_JOIN_APPLICATION_STATUS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CancelJoinApplicationApplicationErrors = {
  code?: 'INVALID_JOIN_APPLICATION_STATUS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type AcceptInviteApplicationErrors =
  | {
      code?: 'INVALID_JOIN_APPLICATION_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateJoinApplicationTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateJoinApplicationTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type PreparePaymentApplicationErrors =
  | {
      code?: 'OP_APP_NOT_INSTALLED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PP_APP_NOT_INSTALLED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NO_PAID_PLAN_CONNECTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ApplyCouponApplicationErrors =
  | {
      code?: 'MISSING_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_IS_ALREADY_APPLIED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_DOES_NOT_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type RemoveCouponApplicationErrors =
  | {
      code?: 'MISSING_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_DOES_NOT_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CompleteFreeCouponPaymentApplicationErrors =
  | {
      code?: 'MISSING_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NO_COUPON_APPLIED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_DOES_NOT_COVER_FULL_AMOUNT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkInviteMembersToProgramByFilterApplicationErrors =
  | {
      code?: 'PROGRAM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PROGRAM_NOT_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    };

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface JoinApplicationCreatedEnvelope {
  entity: JoinApplication;
  metadata: EventMetadata;
}

/**
 * Triggered when a join application is created.
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionId online_programs:participants:v3:join_application:read_events
 * @webhook
 * @eventType wix.online_programs.participants.v3.join_application_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onJoinApplicationCreated(
  handler: (event: JoinApplicationCreatedEnvelope) => void | Promise<void>
): void;

export interface JoinApplicationDeletedEnvelope {
  entity: JoinApplication;
  metadata: EventMetadata;
}

/**
 * Triggered when a join application is deleted.
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionId online_programs:participants:v3:join_application:read_events
 * @webhook
 * @eventType wix.online_programs.participants.v3.join_application_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onJoinApplicationDeleted(
  handler: (event: JoinApplicationDeletedEnvelope) => void | Promise<void>
): void;

export interface JoinApplicationPaymentStatusChangedEnvelope {
  data: PaymentStatusChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when a join application's `paymentStatus` changes.
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionId online_programs:participants:v3:join_application:read_events
 * @webhook
 * @eventType wix.online_programs.participants.v3.join_application_payment_status_changed
 * @slug payment_status_changed
 * @documentationMaturity preview
 */
export declare function onJoinApplicationPaymentStatusChanged(
  handler: (
    event: JoinApplicationPaymentStatusChangedEnvelope
  ) => void | Promise<void>
): void;

export interface JoinApplicationStatusChangedEnvelope {
  data: StatusChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when a join application's `status` changes.
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionId online_programs:participants:v3:join_application:read_events
 * @webhook
 * @eventType wix.online_programs.participants.v3.join_application_status_changed
 * @slug status_changed
 * @documentationMaturity preview
 */
export declare function onJoinApplicationStatusChanged(
  handler: (event: JoinApplicationStatusChangedEnvelope) => void | Promise<void>
): void;

export interface JoinApplicationTagsModifiedEnvelope {
  data: JoinApplicationTagsModified;
  metadata: EventMetadata;
}

/**
 * Triggered when tags on a join application are modified.
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionId online_programs:participants:v3:join_application:read_events
 * @webhook
 * @eventType wix.online_programs.participants.v3.join_application_tags_modified
 * @slug tags_modified
 * @documentationMaturity preview
 */
export declare function onJoinApplicationTagsModified(
  handler: (event: JoinApplicationTagsModifiedEnvelope) => void | Promise<void>
): void;

export interface JoinApplicationUpdatedEnvelope {
  entity: JoinApplication;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a join application is updated.
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionId online_programs:participants:v3:join_application:read_events
 * @webhook
 * @eventType wix.online_programs.participants.v3.join_application_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onJoinApplicationUpdated(
  handler: (event: JoinApplicationUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a JoinApplication for the given program.
 *
 * Members creating their own applications cannot specify `member_id` (it is automatically set to the caller).
 * Site owners with `manage_applications` permission must provide `member_id` when creating applications on behalf of members.
 *
 * For member-initiated applications, `status` is automatically set to `APPROVAL_PENDING` (or `APPROVED` if no approval is required).
 * Site owners can specify `INVITED` or `APPROVED` status when creating applications.
 * @param joinApplication - JoinApplication to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplication
 * @requiredField joinApplication.programId
 * @permissionId online_programs:participants:v3:join_application:create_join_application
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @returns The created JoinApplication.
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.CreateJoinApplication
 */
export async function createJoinApplication(
  joinApplication: NonNullablePaths<JoinApplication, `programId`, 2>
): Promise<
  NonNullablePaths<
    JoinApplication,
    | `programId`
    | `status`
    | `paymentStatus`
    | `paymentInfo.paidPlanIds`
    | `tags.privateTags.tagIds`,
    4
  > & {
    __applicationErrorsType?: CreateJoinApplicationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplication: joinApplication,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.createJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.joinApplication!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplication: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplication']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a JoinApplication.
 *
 * Members can retrieve their own join applications. Site owners with appropriate permissions can retrieve any join application.
 * @param joinApplicationId - ID of the JoinApplication to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:get_join_application
 * @applicableIdentity APP
 * @returns The requested JoinApplication.
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.GetJoinApplication
 */
export async function getJoinApplication(
  joinApplicationId: string
): Promise<
  NonNullablePaths<
    JoinApplication,
    | `programId`
    | `status`
    | `paymentStatus`
    | `paymentInfo.paidPlanIds`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.getJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.joinApplication!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes a JoinApplication.
 *
 * Deleting a JoinApplication permanently removes them from the JoinApplication List.
 * @param joinApplicationId - Id of the JoinApplication to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:delete_join_application
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.DeleteJoinApplication
 */
export async function deleteJoinApplication(
  joinApplicationId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.deleteJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 1,000 join applications, given the provided paging, filtering, and sorting.
 *
 * Members can retrieve their own join applications. Site owners with appropriate permissions can retrieve applications across all members.
 *
 * Query Join Applications runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * To learn about working with query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId online_programs:participants:v3:join_application:query_join_applications
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.QueryJoinApplications
 */
export function queryJoinApplications(): JoinApplicationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    JoinApplication,
    'CURSOR',
    QueryJoinApplicationsRequest,
    QueryJoinApplicationsResponse
  >({
    func: async (payload: QueryJoinApplicationsRequest) => {
      const reqOpts =
        ambassadorWixOnlineProgramsParticipantsV3JoinApplication.queryJoinApplications(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryJoinApplicationsRequest['query']) => {
      const args = [query, {}] as [QueryJoinApplicationsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryJoinApplicationsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.joinApplications,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface JoinApplicationsQueryResult extends QueryCursorResult {
  items: JoinApplication[];
  query: JoinApplicationsQueryBuilder;
  next: () => Promise<JoinApplicationsQueryResult>;
  prev: () => Promise<JoinApplicationsQueryResult>;
}

export interface JoinApplicationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'memberId'
      | 'programId'
      | 'status'
      | 'paymentStatus'
      | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | 'memberId' | 'programId' | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_id' | 'memberId' | 'programId' | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_id' | 'memberId' | 'programId' | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_id' | 'memberId' | 'programId' | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_id' | 'memberId' | 'programId' | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'memberId' | 'programId' | 'participantId',
    value: string
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: 'tags.privateTags.tagIds' | 'tags.publicTags.tagIds',
    value: any[]
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'tags.privateTags.tagIds' | 'tags.publicTags.tagIds',
    value: any[]
  ) => JoinApplicationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'memberId'
      | 'programId'
      | 'status'
      | 'paymentStatus'
      | 'participantId',
    value: any
  ) => JoinApplicationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'memberId'
      | 'programId'
      | 'participantId'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds',
    value: boolean
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'memberId'
      | 'programId'
      | 'status'
      | 'paymentStatus'
      | 'participantId'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds'
    >
  ) => JoinApplicationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'memberId'
      | 'programId'
      | 'status'
      | 'paymentStatus'
      | 'participantId'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds'
    >
  ) => JoinApplicationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => JoinApplicationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => JoinApplicationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<JoinApplicationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.QueryJoinApplications
 * @requiredField query
 */
export async function typedQueryJoinApplications(
  query: JoinApplicationQuery
): Promise<
  NonNullablePaths<
    QueryJoinApplicationsResponse,
    | `joinApplications`
    | `joinApplications.${number}.programId`
    | `joinApplications.${number}.status`
    | `joinApplications.${number}.paymentStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.queryJoinApplications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface JoinApplicationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id', 'memberId', 'participantId', 'programId'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['_createdDate', '_updatedDate'];
      operators: [];
      sort: 'BOTH';
    },
    {
      fields: ['paymentStatus', 'status'];
      operators: ['$eq', '$eq', '$in', '$in', '$nin', '$nin'];
      sort: 'BOTH';
    },
    {
      fields: ['tags.privateTags.tagIds', 'tags.publicTags.tagIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  JoinApplication,
  JoinApplicationQuerySpec
>;
export type JoinApplicationQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 100 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    JoinApplication,
    JoinApplicationQuerySpec,
    JoinApplicationQuery
  >(),
  search: /*#__PURE__*/ createSearchUtils<
    JoinApplication,
    JoinApplicationSearchSpec,
    JoinApplicationSearch
  >(),
};

/**
 * Searches join applications with text-based filtering across member information (email, name, nickname) and supports aggregation.
 *
 * Returns matching applications with cursor-based pagination.
 * @param search - WQL expression.
 * @public
 * @documentationMaturity preview
 * @requiredField search
 * @permissionId online_programs:participants:v3:join_application:search_join_applications
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.SearchJoinApplications
 */
export async function searchJoinApplications(
  search: JoinApplicationSearch
): Promise<
  NonNullablePaths<
    SearchJoinApplicationsResponse,
    | `joinApplications`
    | `joinApplications.${number}.programId`
    | `joinApplications.${number}.status`
    | `joinApplications.${number}.paymentStatus`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`
    | `aggregationData.results.${number}.fieldPath`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.searchJoinApplications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface JoinApplicationSearchSpec extends SearchSpec {
  searchable: [
    'member.email',
    'member.firstName',
    'member.lastName',
    'member.nickname'
  ];
  aggregatable: [
    '_id',
    'member.contactId',
    'memberId',
    'participantId',
    'paymentStatus',
    'programId',
    'status'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: '*';
      fields: [
        '_id',
        'member.contactId',
        'member.email',
        'member.firstName',
        'member.lastName',
        'member.nickname',
        'memberId',
        'participantId',
        'programId'
      ];
      sort: 'BOTH';
    },
    {
      operators: ['$eq', '$in', '$nin'];
      fields: ['paymentStatus', 'status'];
      sort: 'BOTH';
    },
    {
      operators: [];
      fields: ['_createdDate', '_updatedDate'];
      sort: 'BOTH';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  JoinApplication,
  JoinApplicationSearchSpec
>;
export type JoinApplicationSearch = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  List of sort objects.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Aggregations are a way to explore large amounts of data by displaying summaries about various partitions of the data and later allowing to narrow the navigation to a specific partition. 
  @maxSize: 10 
  */
  aggregations?: {
    /** 
  Value aggregation.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  Range aggregation.  
  */
    range?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['range'];
    /** 
  Scalar aggregation.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  Date histogram aggregation.  
  */
    dateHistogram?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['dateHistogram'];
    /** 
  Nested aggregation.  
  */
    nested?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['nested'];
    /** 
  User-defined name of aggregation, should be unique, will appear in aggregation results. 
  @maxLength: 100 
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation, client must provide matching aggregation field below.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Field to aggregate by, use dot notation to specify json path. 
  @maxLength: 200 
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Defines how separate search terms in `expression` are combined.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression. 
  @maxLength: 100 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If empty - will search in all searchable fields. Use dot notation to specify json path. 
  @maxLength: 200,
  @maxSize: 20 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm).  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
  /** 
  UTC offset or IANA time zone. Valid values are
  ISO 8601 UTC offsets, such as +02:00 or -06:00,
  and IANA time zone IDs, such as Europe/Rome.

  Affects all filters and aggregations returned values.
  You may override this behavior in a specific filter by providing
  timestamps including time zone. For example, `"2023-12-20T10:52:34.795Z"`. 
  @maxLength: 50 
  */
  timeZone?: CommonSearchWithEntityContext['timeZone'] | null;
};

/**
 * Create multiple JoinApplications in a single request. Works synchronously.
 * @param joinApplications - List of JoinApplications to be created
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplications
 * @requiredField joinApplications.memberId
 * @requiredField joinApplications.programId
 * @requiredField joinApplications.status
 * @permissionId online_programs:participants:v3:join_application:bulk_create_join_applications
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkCreateJoinApplications
 */
export async function bulkCreateJoinApplications(
  joinApplications: NonNullablePaths<
    JoinApplication,
    `memberId` | `programId` | `status`,
    2
  >[],
  options?: BulkCreateJoinApplicationsOptions
): Promise<
  NonNullablePaths<
    BulkCreateJoinApplicationsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.programId`
    | `results.${number}.item.status`
    | `results.${number}.item.paymentStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkCreateJoinApplicationsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplications: joinApplications,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.bulkCreateJoinApplications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          joinApplications: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['joinApplications', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateJoinApplicationsOptions {
  /** set to `true` if you wish to receive back the created JoinApplications in the response */
  returnEntity?: boolean;
}

/**
 * Delete multiple JoinApplications in a single request. Works synchronously.
 * @param joinApplicationIds - JoinApplication ids to be deleted
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationIds
 * @permissionId online_programs:participants:v3:join_application:bulk_delete_join_applications
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkDeleteJoinApplications
 */
export async function bulkDeleteJoinApplications(
  joinApplicationIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteJoinApplicationsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationIds: joinApplicationIds,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.bulkDeleteJoinApplications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Returns the caller member's JoinApplication for a given program.
 *
 * Uses implicit member identity from call scope. Returns NOT_FOUND if none exists.
 * @param programId - Program ID to look up the caller's join application for
 * @public
 * @documentationMaturity preview
 * @requiredField programId
 * @permissionId online_programs:participants:v3:join_application:get_current_join_application
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.GetCurrentJoinApplication
 */
export async function getCurrentJoinApplication(programId: string): Promise<
  NonNullablePaths<
    GetCurrentJoinApplicationResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: GetCurrentJoinApplicationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programId: programId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.getCurrentJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { programId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['programId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deprecated. Use [GetCurrentJoinApplication](https://dev.wix.com/docs/api-reference/business-management/online-programs/join-applications/get-current-join-application).
 *
 * Returns the caller member's JoinApplication for a given program.
 *
 * Uses implicit member identity from call scope. Returns NOT_FOUND if none exists.
 * @param programId - Program ID to look up the caller's join application for
 * @public
 * @documentationMaturity preview
 * @requiredField programId
 * @permissionId online_programs:participants:v3:join_application:my_join_application
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.MyJoinApplication
 * @deprecated
 * @replacedBy wix.onlineprograms.joinapplications.v3.JoinApplicationsService.GetCurrentJoinApplication
 */
export async function myJoinApplication(programId: string): Promise<
  NonNullablePaths<
    MyJoinApplicationResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: MyJoinApplicationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programId: programId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.myJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { programId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['programId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Approves a JoinApplication.
 *
 * Updates the status from `APPROVAL_PENDING` to `APPROVED`.
 * Returns an error if the JoinApplication is not in `APPROVAL_PENDING` status.
 *
 * After approval, if the program requires payment, the join application's `payment_status` will be updated accordingly
 * and members will need to complete payment before becoming participants.
 * For free programs, a participant is created immediately.
 * @param joinApplicationId - ID of the JoinApplication to approve
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:approve_join_application
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.ApproveJoinApplication
 */
export async function approveJoinApplication(
  joinApplicationId: string
): Promise<
  NonNullablePaths<
    ApproveJoinApplicationResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: ApproveJoinApplicationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.approveJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Declines a JoinApplication.
 *
 * Updates the status from APPROVAL_PENDING to DECLINED.
 * Returns an error if the JoinApplication is not in APPROVAL_PENDING status.
 * @param joinApplicationId - ID of the JoinApplication to decline
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:decline_join_application
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.DeclineJoinApplication
 */
export async function declineJoinApplication(
  joinApplicationId: string
): Promise<
  NonNullablePaths<
    DeclineJoinApplicationResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: DeclineJoinApplicationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.declineJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Cancels a JoinApplication.
 *
 * Updates the status from `APPROVAL_PENDING` to `CANCELLED`.
 * Can only cancel applications in `APPROVAL_PENDING` status. If payment was initiated, it will be cancelled.
 * Applications in other statuses cannot be cancelled.
 * @param joinApplicationId - ID of the JoinApplication to cancel
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:cancel_join_application
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.CancelJoinApplication
 */
export async function cancelJoinApplication(joinApplicationId: string): Promise<
  NonNullablePaths<
    CancelJoinApplicationResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: CancelJoinApplicationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.cancelJoinApplication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Accepts an invitation to join a program.
 *
 * Uses implicit member identity from call scope to verify ownership of the JoinApplication,
 * or `manage_applications` permission for administrative flows, and transitions its status
 * from `INVITED` to `APPROVED`.
 *
 * After accepting the invitation, if the program requires payment, call `PreparePayment` to initiate the payment flow.
 * For free programs, a participant is created automatically and the `participant_id` will be populated.
 *
 * Returns an error if:
 * - The JoinApplication does not exist or the caller does not own it and lacks manage permission
 * - The JoinApplication is not in `INVITED` status
 * @param joinApplicationId - ID of the JoinApplication to accept
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:accept_invite
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.AcceptInvite
 */
export async function acceptInvite(joinApplicationId: string): Promise<
  NonNullablePaths<
    AcceptInviteResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: AcceptInviteApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.acceptInvite(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates tags for multiple JoinApplications by IDs.
 *
 * Allows assigning and/or unassigning tags in a single operation.
 * At least one of assign_tags or unassign_tags must be provided.
 * @param joinApplicationIds - JoinApplication IDs to update tags for (1-100 items)
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationIds
 * @permissionId online_programs:participants:v3:join_application:bulk_update_join_application_tags
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkUpdateJoinApplicationTags
 */
export async function bulkUpdateJoinApplicationTags(
  joinApplicationIds: string[],
  options?: BulkUpdateJoinApplicationTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateJoinApplicationTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateJoinApplicationTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationIds: joinApplicationIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.bulkUpdateJoinApplicationTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          joinApplicationIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateJoinApplicationTagsOptions {
  /** Tags to assign to the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  assignTags?: Tags;
  /** Tags to unassign from the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  unassignTags?: Tags;
}

/**
 * Updates tags for JoinApplications matching a filter.
 *
 * Allows assigning and/or unassigning tags for all JoinApplications matching the filter.
 * At least one of `assign_tags` or `unassign_tags` must be provided.
 * This operation runs asynchronously.
 * Returns a job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
 * @param filter - WQL filter to select JoinApplications
 * @public
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId online_programs:participants:v3:join_application:bulk_update_join_application_tags_by_filter
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkUpdateJoinApplicationTagsByFilter
 */
export async function bulkUpdateJoinApplicationTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateJoinApplicationTagsByFilterOptions
): Promise<
  NonNullablePaths<
    BulkUpdateJoinApplicationTagsByFilterResponse,
    `jobId`,
    2
  > & {
    __applicationErrorsType?: BulkUpdateJoinApplicationTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.bulkUpdateJoinApplicationTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateJoinApplicationTagsByFilterOptions {
  /** Tags to assign to matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  assignTags?: Tags;
  /** Tags to unassign from matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  unassignTags?: Tags;
}

/**
 * Prepares payment for a JoinApplication or resolves an existing Pricing Plans entitlement.
 *
 * Before creating or refreshing checkout details, checks whether the member has
 * an eligible Pricing Plans entitlement for the program. If so, returns the
 * JoinApplication with `payment_status` set to `PAYMENT_SUCCESSFUL` and the
 * eligible plan IDs in `payment_info.paid_plan_ids`. No checkout is needed,
 * even when the application was previously in `PAYMENT_PENDING`.
 *
 * If no eligible entitlement exists, the JoinApplication must have
 * `payment_status` set to `PAYMENT_PENDING`, `PAYMENT_FAILED`, or
 * `PAYMENT_CANCELED`.
 *
 * For `SINGLE_PAYMENT`, creates a Wix Payments order and updates
 * `payment_status` to `PAYMENT_IN_PROGRESS`.
 *
 * For `PAID_PLANS`, validates that pricing plans are connected and updates
 * `payment_status` to `PAYMENT_IN_PROGRESS`.
 *
 * When called for `PAYMENT_FAILED` or `PAYMENT_CANCELED`, this retries payment
 * by replacing stale payment details with newly prepared payment details.
 *
 * Inspect the returned `payment_status` before starting checkout. Direct the
 * member to checkout only when it is `PAYMENT_IN_PROGRESS`. Use `ApplyCoupon`
 * to apply discounts before completing a Wix Payments checkout.
 * @param joinApplicationId - ID of the JoinApplication to prepare payment for
 * @param paymentType - Type of payment to prepare
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @requiredField paymentType
 * @permissionId online_programs:participants:v3:join_application:prepare_payment
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.PreparePayment
 */
export async function preparePayment(
  joinApplicationId: string,
  paymentType: PaymentTypeWithLiterals
): Promise<
  NonNullablePaths<
    PreparePaymentResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: PreparePaymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
    paymentType: paymentType,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.preparePayment(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          joinApplicationId: '$[0]',
          paymentType: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId', 'paymentType']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Applies a coupon to the payment order for a JoinApplication.
 *
 * Applies a coupon code to reduce the payment amount.
 * The JoinApplication must have `payment_status` set to `PAYMENT_IN_PROGRESS`,
 * `PAYMENT_FAILED`, or `PAYMENT_CANCELED`. The latter two let a member apply
 * or change a coupon when retrying after a failed or canceled payment.
 * Returns the updated order totals with discount applied.
 * Only one coupon can be applied per order.
 * @param joinApplicationId - ID of the JoinApplication to apply the coupon to
 * @param couponCode - Coupon code to apply
 * @public
 * @documentationMaturity preview
 * @requiredField couponCode
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:apply_coupon
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.ApplyCoupon
 */
export async function applyCoupon(
  joinApplicationId: string,
  couponCode: string
): Promise<
  NonNullablePaths<
    ApplyCouponResponse,
    `couponId` | `subTotal` | `discount` | `total`,
    2
  > & {
    __applicationErrorsType?: ApplyCouponApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
    couponCode: couponCode,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.applyCoupon(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          joinApplicationId: '$[0]',
          couponCode: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId', 'couponCode']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes a previously applied coupon from the payment order for a JoinApplication.
 *
 * The JoinApplication must have a prepared payment order with a coupon applied,
 * and `payment_status` set to `PAYMENT_IN_PROGRESS`, `PAYMENT_FAILED`, or
 * `PAYMENT_CANCELED`. The latter two let a member clear a coupon when retrying
 * after a failed or canceled payment.
 * Returns the updated order totals after removing the coupon.
 * @param joinApplicationId - ID of the JoinApplication to remove the coupon from
 * @param couponId - ID of the coupon to remove (must match the coupon applied to the order)
 * @public
 * @documentationMaturity preview
 * @requiredField couponId
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:remove_coupon
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.RemoveCoupon
 */
export async function removeCoupon(
  joinApplicationId: string,
  couponId: string
): Promise<
  NonNullablePaths<RemoveCouponResponse, `total`, 2> & {
    __applicationErrorsType?: RemoveCouponApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
    couponId: couponId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.removeCoupon(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          joinApplicationId: '$[0]',
          couponId: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId', 'couponId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Completes payment for a JoinApplication when a 100% discount coupon is applied.
 *
 * This endpoint verifies that a coupon has been applied and covers the full payment amount (order total is zero),
 * then marks the payment as successful and redeems the coupon.
 *
 * Prerequisites:
 * - `PreparePayment` must have been called to create a payment order
 * - `ApplyCoupon` must have been called with a coupon that covers 100% of the price
 * - The JoinApplication `payment_status` must be `PAYMENT_IN_PROGRESS`,
 * `PAYMENT_FAILED`, or `PAYMENT_CANCELED`. The latter two let a member close out
 * a free-coupon retry after a failed or canceled payment without re-running
 * `PreparePayment`.
 *
 * On success, the `payment_status` is updated to `PAYMENT_SUCCESSFUL` and a participant is created.
 * @param joinApplicationId - ID of the JoinApplication to complete free coupon payment for
 * @public
 * @documentationMaturity preview
 * @requiredField joinApplicationId
 * @permissionId online_programs:participants:v3:join_application:complete_free_coupon_payment
 * @permissionId online_programs:participants:v3:join_application:manage_applications
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.CompleteFreeCouponPayment
 */
export async function completeFreeCouponPayment(
  joinApplicationId: string
): Promise<
  NonNullablePaths<
    CompleteFreeCouponPaymentResponse,
    | `joinApplication.programId`
    | `joinApplication.status`
    | `joinApplication.paymentStatus`
    | `joinApplication.paymentInfo.paidPlanIds`
    | `joinApplication.tags.privateTags.tagIds`,
    5
  > & {
    __applicationErrorsType?: CompleteFreeCouponPaymentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    joinApplicationId: joinApplicationId,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.completeFreeCouponPayment(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { joinApplicationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['joinApplicationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Bulk invites site members to a program with optional exclude filter.
 *
 * Creates JoinApplications in INVITED status for all site members matching the filter.
 * This operation runs asynchronously.
 * Returns a job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
 * @param programId - Program to which bulk invite members.
 * @public
 * @documentationMaturity preview
 * @requiredField programId
 * @permissionId online_programs:participants:v3:join_application:bulk_invite_members_to_program_by_filter
 * @applicableIdentity APP
 * @fqn wix.onlineprograms.joinapplications.v3.JoinApplicationsService.BulkInviteMembersToProgramByFilter
 */
export async function bulkInviteMembersToProgramByFilter(
  programId: string,
  options?: BulkInviteMembersToProgramByFilterOptions
): Promise<
  NonNullablePaths<BulkInviteMembersToProgramByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkInviteMembersToProgramByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programId: programId,
    excludeMemberIds: options?.excludeMemberIds,
  });

  const reqOpts =
    ambassadorWixOnlineProgramsParticipantsV3JoinApplication.bulkInviteMembersToProgramByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          programId: '$[0]',
          excludeMemberIds: '$[1].excludeMemberIds',
        },
        singleArgumentUnchanged: false,
      },
      ['programId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkInviteMembersToProgramByFilterOptions {
  /**
   * Exclude site members.
   * @format GUID
   * @maxSize 100
   */
  excludeMemberIds?: string[];
}
